/**
 * The five **subitem** triggers of the webhook recipes — `7000` (subitem created), `7001` (a new
 * update on a subitem), `7002` (any subitem column changes), `7003` (subitem name changes),
 * `7005` (subitem deleted): **15 automations** on three boards.
 *
 * Two facts make these different from their item counterparts, and both are recorded:
 *
 * 1. A subitem is an item on the board's **subitems board** (`boards.settings.subitems_board_id`,
 *    doc 04's subitem model), so its events carry that board's id — monday's own webhook
 *    payloads agree: *"for subitem events `boardId` is the subitems board"*
 *    (`discovery/api-corpus/webhooks.md` §7 #2).
 * 2. The recipe is nevertheless installed on the **parent** board: all 15 rows sit on
 *    `MK-Media Buyers`, `EZ-Facility-Manager` and `MK-Store-Updates`, none on a "Subitems of …"
 *    board (`discovery/automations/api/automations.json`).
 *
 * `scope: 'subitems'` is how those two facts are reconciled: the matching index keys the
 * automation under its board's subitems board, and nothing else in the engine changes.
 */
import { z } from 'zod';
import { defineTrigger } from '../block.js';
import { ANY_VALUE, columnRefSchema } from '../values.js';
const empty = z.object({});
const subitemTrigger = (input) => defineTrigger({
    id: input.id,
    kind: 'trigger',
    app: 'monday',
    sentencePartial: input.sentencePartial,
    paramsSchema: input.paramsSchema,
    subscribes: input.subscribes,
    scope: 'subitems',
    assumed: true,
    references: () => [],
    matches: input.matches ?? (() => true),
    outputs: (event) => ({
        itemId: event.itemId,
        boardId: event.boardId,
        parentItemId: event.payload.parentItemId ?? null,
        columnId: event.payload.columnId ?? null,
    }),
});
export const subitemCreatedTrigger = subitemTrigger({
    id: 'ez.t.subitem_created',
    sentencePartial: 'When a subitem is created',
    subscribes: ['item.created'],
    paramsSchema: empty,
});
export const subitemDeletedTrigger = subitemTrigger({
    id: 'ez.t.subitem_deleted',
    sentencePartial: 'When a subitem is deleted',
    subscribes: ['item.deleted'],
    paramsSchema: empty,
    // Same rule as `ez.t.item_deleted`: an archive is not a deletion.
    matches: (event) => event.payload.kind !== 'archive' && event.payload.kind !== 'move_to_board',
});
export const subitemNameChangedTrigger = subitemTrigger({
    id: 'ez.t.subitem_name_changed',
    sentencePartial: 'When a subitem name changes',
    subscribes: ['item.updated'],
    paramsSchema: empty,
    // Same predicate as `ez.t.item_name_changed`: `item.updated` carries a `changed` field list.
    matches: (event) => Array.isArray(event.payload.changed) && event.payload.changed.includes('name'),
});
export const subitemUpdateCreatedTrigger = subitemTrigger({
    id: 'ez.t.subitem_update_created',
    sentencePartial: 'When a new update is posted on a subitem',
    subscribes: ['update.created'],
    paramsSchema: empty,
    matches: (event) => event.payload.edited !== true,
});
export const subitemColumnChangedParams = z.object({
    columnId: z.union([z.literal(ANY_VALUE), columnRefSchema]).default(ANY_VALUE),
});
export const subitemColumnChangedTrigger = subitemTrigger({
    id: 'ez.t.subitem_column_changed',
    sentencePartial: 'When any subitem column changes',
    subscribes: ['cell.changed'],
    paramsSchema: subitemColumnChangedParams,
    matches: (event, params) => params.columnId === ANY_VALUE || event.payload.columnId === params.columnId.columnId,
});
export const SUBITEM_TRIGGERS = [
    subitemCreatedTrigger,
    subitemDeletedTrigger,
    subitemNameChangedTrigger,
    subitemUpdateCreatedTrigger,
    subitemColumnChangedTrigger,
];
//# sourceMappingURL=ez.t.subitems.js.map